import React, { useEffect, useRef } from 'react';
import P from 'prop-types';
// eslint-disable-next-line lodash/import-scope
import property from 'lodash/property';
import { Box, CenteredContainer, Text } from '@nerdwallet/currency';
import Image from '@nerdwallet/react-image';
import appConfig from '@nerdwallet/app-config';
import WpEmbed from '@nerdwallet/react-wp-embed';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';
// eslint-disable-next-line lodash/import-scope
import get from 'lodash/get';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import IframeResizer from 'iframe-resizer-react';
import {
  group,
  stringInputSpec,
  stringSelectSpec,
  stringTextAreaSpec,
} from '../../lib/editing-configs';

import UniversalContainer, {
  universalContainerPropTypes,
} from '../universal-container';
import UniversalText from '../universal-text/universal-text';
import styles from './third-party-embed-v2.module.less';
import { isInBuilder } from '../../lib/helpers';

const isValidUrl = (url) => !!url.match(/^(http|https):\/\//);

const scriptEmbedContent = (scriptSrc) => {
  const htmlSnippet = `
    <script
      src="${scriptSrc}">
    </script>
  `;
  if (!scriptSrc) {
    return null;
  }
  return (
    <span
      id="script-embed-container"
      style={{ textAlign: 'left' }}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: htmlSnippet }}
    />
  );
};

const iframeEmbedContent = (iframeSrc, height, width) => {
  return (
    isValidUrl(iframeSrc) && (
      <Box id="iframe-wrapper" className={styles.iframeBox}>
        <IframeResizer
          src={iframeSrc}
          style={{
            width: '100%',
            minWidth: width || '100%',
            height: height || '100%',
          }}
        />
      </Box>
    )
  );
};

const calculatorEmbedContent = (cffId) => {
  const iframeUrl = `${appConfig.SITE_BASE_URL}/blog/cff/?form_id=${cffId}`;

  const defaultEditorOutput = () => {
    const url = window.location.pathname;

    if (isInBuilder(url)) {
      return (
        <CenteredContainer
          className={classNames([
            'calculator__default-output',
            'landing-page-standard__max-widths',
          ])}
        >
          <Box
            className={classNames([
              classes('padding-vertical-5'),
              classes('text-align-center'),
            ])}
          >
            <Text size="large" bold align="center">
              Enter a CFF ID to make a calculator appear
            </Text>
          </Box>
        </CenteredContainer>
      );
    }
    return null;
  };

  if (!cffId) return defaultEditorOutput();

  return (
    <div className={styles.calculator}>
      <WpEmbed src={iframeUrl} />
    </div>
  );
};

const renderContent = (
  embedType,
  iframeSrc,
  scriptSrc,
  cffId,
  height,
  width
) => {
  const type = {
    iframe: iframeEmbedContent(iframeSrc, height, width),
    script: scriptEmbedContent(scriptSrc),
    calculator: calculatorEmbedContent(cffId),
  };

  if (
    (embedType === 'script' && !scriptSrc) ||
    (embedType === 'iframe' && !iframeSrc)
  ) {
    return (
      <Box id="default-empty-src-message" style={{ margin: '2rem 0' }}>
        <Text size="large" bold>
          Enter a {embedType} URL to make the content appear
        </Text>
      </Box>
    );
  }

  return type[embedType];
};

const getAlignment = (alignment) => {
  const type = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  };
  return type[alignment];
};

const ThirdPartyEmbedV2 = ({
  src,
  scriptSrc,
  embedType,
  cffId,
  height,
  heightMobile,
  width,
  widthMobile,
  cardIcon,
  title,
  subtitle,
  alignment,
  container,
  cardContainer,
  size,
  customCss,
}) => {
  const heightValue = (size !== 'lg' && heightMobile) || height;
  const withValue = (size !== 'lg' && widthMobile) || width;

  const contentRef = useRef();
  const iframe = contentRef.current?.querySelector('iframe');
  const intervalRef = useRef();
  const iframeRef = useRef();

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      iframeRef.current = contentRef?.current?.querySelector('iframe');
      if (iframeRef.current && iframeRef.current?.contentDocument) {
        iframeRef.current.contentDocument.head.innerHTML += `<style id="injected-style" >${customCss}</style>`;
        const cssInjector =
          iframe.contentDocument.getElementById('injected-style');
        if (cssInjector) {
          clearInterval(intervalRef.current);
        }
      }
    }, 500);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [customCss, iframe?.contentDocument]);

  return (
    <UniversalContainer {...container}>
      <Box
        className={styles.container}
        style={{ alignItems: getAlignment(alignment) }}
      >
        <UniversalContainer {...cardContainer}>
          <div className={styles.card} ref={contentRef} id="content-wrapper">
            {cardIcon && (
              <Image
                objectFit="contain"
                style={{ maxHeight: 90, width: 'auto' }}
                src={cardIcon}
                alt="NerdWallet Brand"
                backgroundColor="transparent"
                naturalHeight={90}
                naturalWidth={221}
              />
            )}
            <Box className={styles.cardHeading}>
              {title && <UniversalText {...title} />}
              {subtitle && (
                <UniversalText
                  {...subtitle}
                  defaultOptions={{ text: { textType: 'text' } }}
                />
              )}
            </Box>
            {renderContent(
              embedType,
              src,
              scriptSrc,
              cffId,
              heightValue,
              withValue
            )}
          </div>
        </UniversalContainer>
      </Box>
    </UniversalContainer>
  );
};

ThirdPartyEmbedV2.propTypes = {
  embedType: P.oneOf(['script', 'iframe']),
  alignment: P.oneOf(['left', 'center', 'right']),
  cardIcon: P.string,
  container: universalContainerPropTypes,
  cardContainer: universalContainerPropTypes,
  title: UniversalText.propTypes,
  subtitle: UniversalText.propTypes,
  children: P.node,
  src: P.string,
  scriptSrc: P.string,
  cffId: P.string,
  formTitle: P.string,
  height: P.string,
  heightMobile: P.string,
  width: P.string,
  widthMobile: P.string,
  size: P.string,
  disableTopBottomCardColor: P.bool,
  customCss: P.string,
};

ThirdPartyEmbedV2.defaultProps = {
  src: '',
  scriptSrc: '',
  cffId: '',
  formTitle: '',
  embedType: 'iframe',
  height: '100%',
  width: '100%',
  disableTopBottomCardColor: false,
};

ThirdPartyEmbedV2.getEditingConfig = (componentDocument, fieldName = '') => {
  const embedType = fieldName
    ? get(componentDocument.props, `${fieldName}.embedType`, 'iframe')
    : componentDocument?.props?.embedType || 'iframe';

  const getInputName = (name) => {
    if (fieldName) {
      return `${fieldName}.${name}`;
    }
    return name;
  };

  const componentLabel = fieldName
    ? 'Third Party Embed in Grid'
    : 'Third Party Embed';

  const inputConfigMap = {
    script: [
      stringTextAreaSpec(getInputName('scriptSrc'), 'Script Source URL'),
    ],
    iframe: [
      stringInputSpec(getInputName('src'), 'Iframe Source URL'),
      stringInputSpec('height', 'Iframe Height', {
        help: 'Defaults to "100%"',
      }),
      stringInputSpec(getInputName('heightMobile'), 'Iframe Height Mobile'),
      stringInputSpec(getInputName('width'), 'Iframe Width', {
        help: 'Defaults to "100%"',
      }),
      stringInputSpec(getInputName('widthMobile'), 'Iframe Width Mobile'),
    ],
    calculator: [
      stringInputSpec(getInputName('cffId'), 'Calculated Fields Form ID', {
        help: 'For a list of CFFs you can display on landing pages, login to WordPress and navigate to Settings > Calculated Fields Form.',
      }),
    ],
  };

  const embedTypeInputConfig = inputConfigMap[embedType];

  return [
    ...UniversalContainer.getEditingConfig(
      componentDocument,
      'Container',
      [
        'backgroundColor',
        'backgroundImage',
        'border',
        'margin',
        'padding',
        'hide',
        'jumpLinkId',
      ],
      'container'
    ),
    group('Card Container', [
      stringInputSpec(getInputName('cardIcon'), 'Card Icon'),
      ...UniversalContainer.getEditingConfig(
        componentDocument,
        'Card Container',
        ['border', 'margin', 'padding', 'backgroundColor', 'backgroundImage'],
        'cardContainer',
        { disableGroup: true }
      ),
    ]),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Title',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      getInputName('title')
    ),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Subtitle',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      getInputName('subtitle'),
      { text: { textType: 'text' } }
    ),

    group(componentLabel, [
      stringSelectSpec(
        getInputName('embedType'),
        'Embed Type',
        [
          { label: 'Iframe', value: 'iframe' },
          { label: 'Script', value: 'script' },
          { label: 'Calculator', value: 'calculator' },
        ],
        {
          help: 'Defaults to "Iframe"',
        }
      ),
      stringSelectSpec(
        getInputName('alignment'),
        'Content Alignment',
        [
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'right' },
        ],
        {
          help: 'Defaults to "Center"',
        }
      ),
      ...embedTypeInputConfig,
      stringTextAreaSpec(getInputName('customCss'), 'Custom CSS'),
    ]),
  ];
};

ThirdPartyEmbedV2.displayName = 'ThirdPartyEmbedV2';
export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(ThirdPartyEmbedV2);
